<template>
    <v-container fluid class="chc-analysis other-proceedings-shadcn pa-6">
        <header class="chc-analysis__page-header">
            <div>
                <v-breadcrumbs class="app-breadcrumb pa-0 mb-2" divider="›" :items="dataset.breadcrumbItems" />
                <h1>{{ editing ? 'Editar solicitação de CHC' : 'Analisar solicitação de CHC' }}</h1>
                <p>Confira os dados, créditos e documentos antes de registrar a decisão.</p>
            </div>
            <div class="chc-analysis__page-actions">
                        <template v-if="!dataset.loading && dataset.data && dataset.data.id">
                            <v-tooltip v-if="!editing" bottom>
                                <template #activator="{ on }">
                                    <v-btn v-on="on" id="btnEdit" type="button" outlined elevation="0" color="primary"
                                        @click="enableEdit"><v-icon left small>mdi-pencil-outline</v-icon>Editar dados
                                    </v-btn>
                                </template>
                                <span>Habilitar edição dos dados da certidão</span>
                            </v-tooltip>
                            <template v-else>
                                <v-tooltip bottom>
                                    <template #activator="{ on }">
                                        <v-btn v-on="on" id="btnCancel" type="button" text elevation="0" color="primary"
                                            @click="cancelEdit">Cancelar
                                        </v-btn>
                                    </template>
                                    <span>Descartar alterações e voltar para o modo de visualização</span>
                                </v-tooltip>
                                <v-tooltip bottom>
                                    <template #activator="{ on }">
                                        <v-btn v-on="on" id="btnSave" type="button" elevation="0" color="primary"
                                            :loading="dataset.saving" @click="save"><v-icon left small>mdi-content-save-outline</v-icon>Salvar alterações
                                        </v-btn>
                                    </template>
                                    <span>Persistir as alterações realizadas na certidão</span>
                                </v-tooltip>
                            </template>
                        </template>
            </div>
        </header>

        <div v-if="dataset.loading" class="chc-analysis__skeleton">
            <v-skeleton-loader type="heading, list-item-two-line, image, table-heading, table-tbody" />
        </div>

        <template v-else>
            <div class="chc-analysis__summary">
                <div class="chc-analysis__summary-main">
                    <v-chip small :color="getStatusColor(dataset.data.status)" text-color="white">
                        {{ getStatusLabel(dataset.data.status) }}
                    </v-chip>
                    <span class="chc-analysis__protocol">{{ dataset.data.protocol || `CHC-${dataset.data.id}` }}</span>
                    <div class="chc-analysis__requester">
                        <strong>{{ dataset.data.requester_name || 'Requerente não identificado' }}</strong>
                        <span>{{ getRequesterTypeLabel(dataset.data.requester_type) }} · {{ formatCpfCnpj(dataset.data.requester_registration) }}</span>
                    </div>
                    <small><v-icon x-small>mdi-gavel</v-icon>{{ dataset.data.origin_process || 'Processo não informado' }} · atualizado em {{ formatDateTime(dataset.data.updated_at) }}</small>
                </div>
                <v-btn text color="primary" to="/certidao-credito">
                    <v-icon left small>mdi-arrow-left</v-icon>Voltar à fila
                </v-btn>
            </div>

            <div class="chc-analysis__metrics" aria-label="Resumo da solicitação">
                <div>
                    <span class="chc-analysis__metric-icon"><v-icon small>mdi-account-multiple-outline</v-icon></span>
                    <span><small>Credores</small><strong>{{ creditorsCount }}</strong></span>
                </div>
                <div>
                    <span class="chc-analysis__metric-icon"><v-icon small>mdi-cash-multiple</v-icon></span>
                    <span><small>Créditos</small><strong>{{ creditsCount }}</strong></span>
                </div>
                <div>
                    <span class="chc-analysis__metric-icon"><v-icon small>mdi-paperclip</v-icon></span>
                    <span><small>Documentos disponíveis</small><strong>{{ availableDocumentsCount }}</strong></span>
                </div>
            </div>

            <v-form ref="form" v-model="dataset.validForm" lazy-validation class="chc-analysis__grid">
            <v-row class="chc-analysis__main">
                <v-col cols="12">
                    <v-card outlined class="card-form-outlined chc-analysis__section-card">
                        <v-card-title class="card-group-title">
                            <span class="chc-analysis__section-icon"><v-icon small>mdi-file-document-edit-outline</v-icon></span>
                            <span><strong>Dados da certidão</strong><small>Identificação e informações processuais da solicitação.</small></span>
                            <v-spacer></v-spacer>
                            <v-chip small :color="editing ? 'warning' : 'grey lighten-2'" label :text-color="editing ? 'white' : 'grey darken-2'"
                                class="mr-2">
                                <v-icon left small>{{ editing ? 'mdi-pencil' : 'mdi-eye' }}</v-icon>
                                {{ editing ? 'Modo Edição' : 'Modo Visualização' }}
                            </v-chip>
                        </v-card-title>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" sm="4">
                                    <v-text-field dense label="Processo de origem"
                                        v-model="dataset.data.origin_process" outlined hide-details="auto" v-mask="$masks.cnj"
                                        :rules="[$rules.required, $rules.cnj]"
                                        :readonly="!editing">
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" sm="2">
                                    <v-autocomplete dense label="UF do processo" :items="dataset.statesList"
                                        v-model="dataset.data.process_uf" item-value="name" item-text="name"
                                        outlined hide-details="auto" :readonly="!editing"
                                        :menu-props="!editing ? { value: false } : {}">
                                    </v-autocomplete>
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-autocomplete dense label="Ramo da justiça" :items="dataset.justiceBranchTypes"
                                        v-model="dataset.data.justice_branch" item-value="id" item-text="name"
                                        outlined hide-details="auto" :readonly="!editing"
                                        :menu-props="!editing ? { value: false } : {}">
                                    </v-autocomplete>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" sm="6">
                                    <v-text-field dense label="Órgão julgador"
                                        v-model="dataset.data.judging_body" outlined hide-details="auto"
                                        hint="Informe a vara em que tramita o processo" persistent-hint
                                        :readonly="!editing">
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" sm="3">
                                    <v-text-field v-if="editing" dense label="Trânsito em julgado" type="date"
                                        v-model="dataset.data.final_judgment_at" outlined hide-details="auto">
                                    </v-text-field>
                                    <v-text-field v-else dense label="Trânsito em julgado"
                                        :value="formatDate(dataset.data.final_judgment_at)" outlined
                                        hide-details="auto" readonly>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" sm="3">
                                    <v-text-field v-if="editing" dense label="Cálculo atualizado em" type="date"
                                        v-model="dataset.data.calculation_updated_at" outlined hide-details="auto">
                                    </v-text-field>
                                    <v-text-field v-else dense label="Cálculo atualizado em"
                                        :value="formatDate(dataset.data.calculation_updated_at)" outlined
                                        hide-details="auto" readonly>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12">
                                    <v-text-field dense label="Criado em"
                                        :value="formatDateTime(dataset.data.created_at)" outlined
                                        hide-details="auto" readonly>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12">
                                    <v-textarea dense label="Observações" v-model="dataset.data.observations"
                                        outlined hide-details="auto" :readonly="!editing" auto-grow rows="2">
                                    </v-textarea>
                                </v-col>
                            </v-row>
                            <v-row class="mt-n5">
                                <v-col cols="12" sm="4">
                                    <v-checkbox v-model="dataset.data.has_attorney_fees"
                                        :true-value="1" :false-value="0"
                                        label="Possui honorários advocatícios" hide-details
                                        :disabled="!editing"></v-checkbox>
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <v-checkbox v-model="dataset.data.has_expert_fees"
                                        :true-value="1" :false-value="0"
                                        label="Possui honorários periciais" hide-details
                                        :disabled="!editing"></v-checkbox>
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <v-checkbox v-model="dataset.data.is_confidential"
                                        :true-value="1" :false-value="0"
                                        label="Confidencial" hide-details
                                        :disabled="!editing"></v-checkbox>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row class="chc-analysis__decision">
                <v-col cols="12">
                    <v-card outlined class="status-actions-card">
                        <v-card-title class="status-actions-title">
                            <span><strong>Decisão</strong><small>Registre o resultado da análise.</small></span>
                        </v-card-title>
                        <v-card-text>
                            <div class="chc-analysis__decision-status">
                                <span>Status atual</span>
                                <v-chip small :color="getStatusColor(dataset.data.status)" text-color="white">{{ getStatusLabel(dataset.data.status) }}</v-chip>
                            </div>
                            <div class="chc-analysis__decision-actions">
                                <v-tooltip bottom>
                                    <template #activator="{ on }">
                                        <div v-on="on">
                                            <v-btn small id="btnApprove" type="button" elevation="0"
                                                color="success" :loading="dataset.actionLoading === 1"
                                                :disabled="dataset.data.status !== 0 && dataset.data.status !== 5"
                                                @click="changeStatus(1)">
                                                <v-icon left small>mdi-check-circle-outline</v-icon>
                                                Aprovar
                                            </v-btn>
                                        </div>
                                    </template>
                                    <span>Aprovar a certidão de habilitação de crédito</span>
                                </v-tooltip>
                                <v-tooltip bottom>
                                    <template #activator="{ on }">
                                        <div v-on="on">
                                            <v-btn small id="btnReject" type="button" elevation="0"
                                                color="error" :loading="dataset.actionLoading === 2"
                                                :disabled="dataset.data.status !== 0 && dataset.data.status !== 5"
                                                @click="changeStatus(2)">
                                                <v-icon left small>mdi-close-circle-outline</v-icon>
                                                Rejeitar
                                            </v-btn>
                                        </div>
                                    </template>
                                    <span>Rejeitar a certidão de habilitação de crédito</span>
                                </v-tooltip>
                                <v-tooltip bottom>
                                    <template #activator="{ on }">
                                        <div v-on="on">
                                            <v-btn small id="btnRequestDocs" type="button" elevation="0"
                                                color="#7E55AB" class="white--text"
                                                :disabled="dataset.data.status !== 0 && dataset.data.status !== 5"
                                                @click="openRequestDocsModal">
                                                <v-icon left small>mdi-file-document-outline</v-icon>
                                                Solicitar novos documentos
                                            </v-btn>
                                        </div>
                                    </template>
                                    <span>Solicitar ao requerente o envio de novos documentos</span>
                                </v-tooltip>
                                <v-tooltip bottom>
                                    <template #activator="{ on }">
                                        <div v-on="on">
                                            <v-btn small id="btnOutdated" type="button" elevation="0"
                                                color="#FB8C00" class="white--text"
                                                :disabled="dataset.data.status === 6"
                                                @click="changeStatus(6)">
                                                <v-icon left small>mdi-clock-alert-outline</v-icon>
                                                Certidão desatualizada
                                            </v-btn>
                                        </div>
                                    </template>
                                    <span>Suspender a análise e orientar o requerente a atualizar a certidão</span>
                                </v-tooltip>
                                <v-tooltip bottom>
                                    <template #activator="{ on }">
                                        <div v-on="on">
                                            <v-btn small id="btnRestore" type="button" elevation="0"
                                                color="#9C27B0" dark :loading="dataset.actionLoading === 0"
                                                :disabled="dataset.data.status === 0"
                                                @click="changeStatus(0)">
                                                <v-icon left small>mdi-restore</v-icon>
                                                Restaurar
                                            </v-btn>
                                        </div>
                                    </template>
                                    <span>Voltar o status da certidão para pendente</span>
                                </v-tooltip>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card outlined class="card-form-outlined chc-analysis__section-card">
                        <v-card-title class="card-group-title">
                            <span class="chc-analysis__section-icon"><v-icon small>mdi-account-outline</v-icon></span>
                            <span><strong>Requerente</strong><small>Identificação de quem enviou a solicitação.</small></span>
                            <v-spacer></v-spacer>
                        </v-card-title>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" sm="4">
                                    <v-text-field dense label="Tipo"
                                        :value="getRequesterTypeLabel(dataset.data.requester_type)" outlined
                                        hide-details="auto" disabled>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" sm="5">
                                    <v-text-field dense label="Nome"
                                        :value="dataset.data.requester_name" outlined hide-details="auto" disabled>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" sm="3">
                                    <v-text-field dense label="CPF/CNPJ"
                                        :value="formatCpfCnpj(dataset.data.requester_registration)" outlined
                                        hide-details="auto" disabled>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card outlined class="card-form-outlined chc-analysis__section-card">
                        <v-card-title class="card-group-title">
                            <span class="chc-analysis__section-icon"><v-icon small>mdi-office-building-outline</v-icon></span>
                            <span><strong>Devedoras</strong><small>Empresas relacionadas à recuperação judicial.</small></span>
                            <v-spacer></v-spacer>
                            <v-tooltip v-if="editing" bottom>
                                <template #activator="{ on }">
                                    <v-btn v-on="on" small color="primary" outlined @click="addDebtor">
                                        <v-icon left small>mdi-plus</v-icon>Adicionar
                                    </v-btn>
                                </template>
                                <span>Adicionar nova devedora</span>
                            </v-tooltip>
                        </v-card-title>
                        <v-card-text>
                            <v-data-table :headers="getDebtorsHeaders()" :items="dataset.data.debtors || []"
                                hide-default-footer disable-sort no-data-text="Nenhum devedor"
                                class="app-table">
                                <template v-slot:[`item.client`]="{ item }">
                                    <v-autocomplete v-if="editing" dense :items="dataset.clients"
                                        v-model="item.client_id" item-value="id" item-text="name"
                                        hide-details outlined no-data-text="Nenhum caso encontrado">
                                    </v-autocomplete>
                                    <span v-else>{{ getClientName(item.client_id) }}</span>
                                </template>
                                <template v-slot:[`item.actions`]="{ index }">
                                    <v-tooltip bottom>
                                        <template #activator="{ on }">
                                            <v-icon v-on="on" small color="error"
                                                @click="removeDebtor(index)">
                                                mdi-delete-outline
                                            </v-icon>
                                        </template>
                                        <span>Remover esta devedora</span>
                                    </v-tooltip>
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card outlined class="card-form-outlined chc-analysis__section-card">
                        <v-card-title class="card-group-title">
                            <span class="chc-analysis__section-icon"><v-icon small>mdi-account-cash-outline</v-icon></span>
                            <span><strong>Credores</strong><small>Pessoas vinculadas aos créditos informados.</small></span>
                            <v-spacer></v-spacer>
                            <v-tooltip v-if="editing" bottom>
                                <template #activator="{ on }">
                                    <v-btn v-on="on" small color="primary" outlined @click="addCreditor">
                                        <v-icon left small>mdi-plus</v-icon>Adicionar
                                    </v-btn>
                                </template>
                                <span>Adicionar novo credor</span>
                            </v-tooltip>
                        </v-card-title>
                        <v-card-text>
                            <v-data-table :headers="getCreditorsHeaders()" :items="dataset.data.creditors || []"
                                hide-default-footer disable-sort no-data-text="Nenhum credor"
                                class="app-table">
                                <template v-slot:[`item.name`]="{ item }">
                                    <v-text-field v-if="editing" dense v-model="item.name" hide-details outlined>
                                    </v-text-field>
                                    <span v-else>{{ item.name }}</span>
                                </template>
                                <template v-slot:[`item.registration`]="{ item }">
                                    <v-text-field v-if="editing" dense v-model="item.registration"
                                        hide-details="auto" outlined
                                        v-mask="(item.registration || '').length < 15 ? $masks.cpf : $masks.cnpj"
                                        :rules="[$rules.required, cpfCnpjRule]">
                                    </v-text-field>
                                    <span v-else>{{ formatCpfCnpj(item.registration) }}</span>
                                </template>
                                <template v-slot:[`item.actions`]="{ index }">
                                    <v-tooltip bottom>
                                        <template #activator="{ on }">
                                            <v-icon v-on="on" small color="error"
                                                @click="removeCreditor(index)">
                                                mdi-delete-outline
                                            </v-icon>
                                        </template>
                                        <span>Remover este credor</span>
                                    </v-tooltip>
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card outlined class="card-form-outlined chc-analysis__section-card">
                        <v-card-title class="card-group-title">
                            <span class="chc-analysis__section-icon"><v-icon small>mdi-cash-multiple</v-icon></span>
                            <span><strong>Créditos</strong><small>Valores, classes, moedas e resultado da revisão.</small></span>
                            <v-spacer></v-spacer>
                            <v-tooltip v-if="editing" bottom>
                                <template #activator="{ on }">
                                    <v-btn v-on="on" small color="primary" outlined @click="addCredit">
                                        <v-icon left small>mdi-plus</v-icon>Adicionar
                                    </v-btn>
                                </template>
                                <span>Adicionar novo crédito</span>
                            </v-tooltip>
                        </v-card-title>
                        <v-card-text>
                            <v-data-table :headers="getCreditsHeaders()" :items="dataset.data.credits || []"
                                hide-default-footer disable-sort no-data-text="Nenhum crédito"
                                class="app-table">
                                <template v-slot:[`item.registration`]="{ item }">
                                    <v-text-field v-if="editing" dense v-model="item.registration"
                                        hide-details="auto" outlined
                                        v-mask="(item.registration || '').length < 15 ? $masks.cpf : $masks.cnpj"
                                        :rules="[$rules.required, cpfCnpjRule]">
                                    </v-text-field>
                                    <span v-else>{{ formatCpfCnpj(item.registration) }}</span>
                                </template>
                                <template v-slot:[`item.currency`]="{ item }">
                                    <v-select v-if="editing" dense :items="dataset.currencyTypes"
                                        v-model="item.currency" item-value="id" item-text="name" hide-details
                                        outlined>
                                    </v-select>
                                    <span v-else>{{ getCurrencyName(item.currency) }}</span>
                                </template>
                                <template v-slot:[`item.credit_origin`]="{ item }">
                                    <v-select v-if="editing" dense :items="dataset.creditOriginTypes"
                                        v-model="item.credit_origin" item-value="id" item-text="name" hide-details
                                        outlined>
                                    </v-select>
                                    <span v-else>{{ item.credit_origin || 'N/A' }}</span>
                                </template>
                                <template v-slot:[`item.amount`]="{ item }">
                                    <v-currency-field v-if="editing" dense v-model="item.amount"
                                        :allowNegative="false" hide-details outlined>
                                    </v-currency-field>
                                    <span v-else>{{ formatAmount(item.amount) }}</span>
                                </template>
                                <template v-slot:[`item.class_type`]="{ item }">
                                    <v-select v-if="editing" dense :items="dataset.creditorTypes"
                                        v-model="item.class_type" item-value="id" item-text="name" hide-details
                                        outlined>
                                    </v-select>
                                    <v-chip v-else small label text-color="primary">
                                        {{ getTypeName(item.class_type) }}
                                    </v-chip>
                                </template>
                                <template v-slot:[`item.reviewed_class_type`]="{ item }">
                                    <v-select v-if="editing" dense :items="dataset.creditorTypes"
                                        v-model="item.reviewed_class_type" item-value="id" item-text="name" hide-details
                                        outlined>
                                    </v-select>
                                    <v-chip v-else small label text-color="primary">
                                        {{ getTypeName(item.reviewed_class_type) }}
                                    </v-chip>
                                </template>
                                <template v-slot:[`item.review_status`]="{ item }">
                                    <v-select v-if="editing" dense :items="dataset.reviewStatusTypes"
                                        v-model="item.review_status" item-value="id" item-text="name" hide-details
                                        outlined>
                                    </v-select>
                                    <v-chip v-else small label :color="getReviewStatusColor(item.review_status)" text-color="white">
                                        {{ getReviewStatusLabel(item.review_status) }}
                                    </v-chip>
                                </template>
                                <template v-slot:[`item.review_note`]="{ item }">
                                    <v-text-field v-if="editing" dense v-model="item.review_note" hide-details outlined>
                                    </v-text-field>
                                    <span v-else>{{ item.review_note || 'N/A' }}</span>
                                </template>
                                <template v-slot:[`item.actions`]="{ index }">
                                    <v-tooltip bottom>
                                        <template #activator="{ on }">
                                            <v-icon v-on="on" small color="error"
                                                @click="removeCredit(index)">
                                                mdi-delete-outline
                                            </v-icon>
                                        </template>
                                        <span>Remover este crédito</span>
                                    </v-tooltip>
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row v-if="dataset.data.has_attorney_fees">
                <v-col cols="12">
                    <v-card outlined class="card-form-outlined chc-analysis__section-card">
                        <v-card-title class="card-group-title">
                            <span class="chc-analysis__section-icon"><v-icon small>mdi-briefcase-outline</v-icon></span>
                            <span><strong>Honorários advocatícios</strong><small>Profissionais e valores relacionados.</small></span>
                            <v-spacer></v-spacer>
                            <v-tooltip v-if="editing" bottom>
                                <template #activator="{ on }">
                                    <v-btn v-on="on" small color="primary" outlined @click="addAttorneyFee">
                                        <v-icon left small>mdi-plus</v-icon>Adicionar
                                    </v-btn>
                                </template>
                                <span>Adicionar novo honorário advocatício</span>
                            </v-tooltip>
                        </v-card-title>
                        <v-card-text>
                            <v-data-table :headers="getAttorneyFeesHeaders()"
                                :items="dataset.data.attorneyFees || []" hide-default-footer disable-sort
                                no-data-text="Nenhum honorário advocatício" class="app-table">
                                <template v-slot:[`item.name`]="{ item }">
                                    <v-text-field v-if="editing" dense v-model="item.name" hide-details outlined>
                                    </v-text-field>
                                    <span v-else>{{ item.name || 'N/A' }}</span>
                                </template>
                                <template v-slot:[`item.registration`]="{ item }">
                                    <v-text-field v-if="editing" dense v-model="item.registration"
                                        hide-details="auto" outlined v-mask="$masks.cpf" :rules="[$rules.required, $rules.cpf]">
                                    </v-text-field>
                                    <span v-else>{{ formatCpfCnpj(item.registration) }}</span>
                                </template>
                                <template v-slot:[`item.oab`]="{ item }">
                                    <v-text-field v-if="editing" dense v-model="item.oab" hide-details outlined>
                                    </v-text-field>
                                    <span v-else>{{ item.oab }}</span>
                                </template>
                                <template v-slot:[`item.uf`]="{ item }">
                                    <v-autocomplete v-if="editing" dense :items="dataset.statesList"
                                        v-model="item.uf" item-value="name" item-text="name" hide-details outlined>
                                    </v-autocomplete>
                                    <span v-else>{{ item.uf }}</span>
                                </template>
                                <template v-slot:[`item.amount`]="{ item }">
                                    <v-currency-field v-if="editing" dense v-model="item.amount"
                                        :allowNegative="false" hide-details outlined>
                                    </v-currency-field>
                                    <span v-else>{{ formatAmount(item.amount) }}</span>
                                </template>
                                <template v-slot:[`item.fixed_at`]="{ item }">
                                    <v-text-field v-if="editing" dense type="date" v-model="item.fixed_at"
                                        hide-details outlined>
                                    </v-text-field>
                                    <span v-else>{{ formatDate(item.fixed_at) }}</span>
                                </template>
                                <template v-slot:[`item.actions`]="{ index }">
                                    <v-tooltip bottom>
                                        <template #activator="{ on }">
                                            <v-icon v-on="on" small color="error"
                                                @click="removeAttorneyFee(index)">
                                                mdi-delete-outline
                                            </v-icon>
                                        </template>
                                        <span>Remover este honorário advocatício</span>
                                    </v-tooltip>
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row v-if="dataset.data.has_expert_fees">
                <v-col cols="12">
                    <v-card outlined class="card-form-outlined chc-analysis__section-card">
                        <v-card-title class="card-group-title">
                            <span class="chc-analysis__section-icon"><v-icon small>mdi-calculator-variant-outline</v-icon></span>
                            <span><strong>Honorários periciais</strong><small>Peritos e valores fixados no processo.</small></span>
                            <v-spacer></v-spacer>
                            <v-tooltip v-if="editing" bottom>
                                <template #activator="{ on }">
                                    <v-btn v-on="on" small color="primary" outlined @click="addExpertFee">
                                        <v-icon left small>mdi-plus</v-icon>Adicionar
                                    </v-btn>
                                </template>
                                <span>Adicionar novo honorário pericial</span>
                            </v-tooltip>
                        </v-card-title>
                        <v-card-text>
                            <v-data-table :headers="getExpertFeesHeaders()"
                                :items="dataset.data.expertFees || []" hide-default-footer disable-sort
                                no-data-text="Nenhum honorário pericial" class="app-table">
                                <template v-slot:[`item.name`]="{ item }">
                                    <v-text-field v-if="editing" dense v-model="item.name" hide-details outlined>
                                    </v-text-field>
                                    <span v-else>{{ item.name || 'N/A' }}</span>
                                </template>
                                <template v-slot:[`item.registration`]="{ item }">
                                    <v-text-field v-if="editing" dense v-model="item.registration"
                                        hide-details="auto" outlined
                                        v-mask="(item.registration || '').length < 15 ? $masks.cpf : $masks.cnpj"
                                        :rules="[$rules.required, cpfCnpjRule]">
                                    </v-text-field>
                                    <span v-else>{{ formatCpfCnpj(item.registration) }}</span>
                                </template>
                                <template v-slot:[`item.amount`]="{ item }">
                                    <v-currency-field v-if="editing" dense v-model="item.amount"
                                        :allowNegative="false" hide-details outlined>
                                    </v-currency-field>
                                    <span v-else>{{ formatAmount(item.amount) }}</span>
                                </template>
                                <template v-slot:[`item.fixed_at`]="{ item }">
                                    <v-text-field v-if="editing" dense type="date" v-model="item.fixed_at"
                                        hide-details outlined>
                                    </v-text-field>
                                    <span v-else>{{ formatDate(item.fixed_at) }}</span>
                                </template>
                                <template v-slot:[`item.actions`]="{ index }">
                                    <v-tooltip bottom>
                                        <template #activator="{ on }">
                                            <v-icon v-on="on" small color="error"
                                                @click="removeExpertFee(index)">
                                                mdi-delete-outline
                                            </v-icon>
                                        </template>
                                        <span>Remover este honorário pericial</span>
                                    </v-tooltip>
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card outlined class="card-form-outlined chc-analysis__section-card chc-analysis__documents">
                        <v-card-title class="card-group-title"><span class="chc-analysis__section-icon"><v-icon small>mdi-folder-outline</v-icon></span><span><strong>Documentos</strong><small>Arquivos apresentados para subsidiar a análise.</small></span></v-card-title>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" sm="6" class="chc-analysis__document-card">
                                    <div class="font-weight-bold mb-2">Certidão de Habilitação de Crédito</div>
                                    <div v-if="dataset.data.chc_file_url">
                                        <div class="mb-2">{{ dataset.data.chc_file_name }}</div>
                                        <v-tooltip bottom>
                                            <template #activator="{ on }">
                                                <v-btn v-on="on" type="button" elevation="0" color="primary"
                                                    outlined :href="dataset.data.chc_file_url" target="_blank">
                                                    Visualizar documento
                                                </v-btn>
                                            </template>
                                            <span>Abrir a Certidão de Habilitação de Crédito em uma nova aba</span>
                                        </v-tooltip>
                                    </div>
                                    <div v-else class="text--secondary">Nenhum documento</div>
                                </v-col>
                                <v-col cols="12" sm="6" class="chc-analysis__document-card" v-if="dataset.data.is_confidential">
                                    <div class="font-weight-bold mb-2">Processo completo (confidencial)</div>
                                    <div v-if="dataset.data.confidential_full_process_file_url">
                                        <div class="mb-2">{{ dataset.data.confidential_full_process_file_name }}</div>
                                        <v-tooltip bottom>
                                            <template #activator="{ on }">
                                                <v-btn v-on="on" type="button" elevation="0" color="primary"
                                                    outlined :href="dataset.data.confidential_full_process_file_url"
                                                    target="_blank">
                                                    Visualizar documento
                                                </v-btn>
                                            </template>
                                            <span>Abrir o processo completo confidencial em uma nova aba</span>
                                        </v-tooltip>
                                    </div>
                                    <div v-else class="text--secondary">Nenhum documento</div>
                                </v-col>
                                <v-col cols="12" sm="6" class="chc-analysis__document-card">
                                    <div class="font-weight-bold mb-2">Decisão homologatória de cálculo</div>
                                    <div v-if="dataset.data.homologation_decision_file_url">
                                        <div class="mb-2">{{ dataset.data.homologation_decision_file_name }}</div>
                                        <v-btn type="button" elevation="0" color="primary" outlined
                                            :href="dataset.data.homologation_decision_file_url" target="_blank">
                                            Visualizar documento
                                        </v-btn>
                                    </div>
                                    <div v-else class="text--secondary">Nenhum documento</div>
                                </v-col>
                                <v-col cols="12" sm="6" class="chc-analysis__document-card">
                                    <div class="font-weight-bold mb-2">Planilha de cálculos</div>
                                    <div v-if="dataset.data.calculation_spreadsheet_file_url">
                                        <div class="mb-2">{{ dataset.data.calculation_spreadsheet_file_name }}</div>
                                        <v-btn type="button" elevation="0" color="primary" outlined
                                            :href="dataset.data.calculation_spreadsheet_file_url" target="_blank">
                                            Visualizar documento
                                        </v-btn>
                                    </div>
                                    <div v-else class="text--secondary">Nenhum documento</div>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
            </v-form>
        </template>

        <v-dialog v-model="requestDocsModal.show" max-width="520" persistent content-class="chc-analysis-dialog">
            <v-card class="chc-analysis__dialog-card">
                <v-card-title>Solicitar novos documentos</v-card-title>
                <v-card-text>
                    <p>Selecione os documentos necessários para continuar a análise.</p>
                    <label class="chc-analysis__document-option">
                        <v-checkbox v-model="requestDocsModal.chc" hide-details />
                        <span><strong>Certidão de habilitação de crédito</strong><small>Solicita uma nova versão da certidão apresentada.</small></span>
                    </label>
                    <label class="chc-analysis__document-option">
                        <v-checkbox v-model="requestDocsModal.fullProcess" hide-details />
                        <span><strong>Processo integral</strong><small>Solicita a íntegra dos autos para conferência.</small></span>
                    </label>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text :disabled="requestDocsModal.loading" @click="closeRequestDocsModal">Cancelar</v-btn>
                    <v-btn color="primary" elevation="0" :loading="requestDocsModal.loading"
                        :disabled="!requestDocsModal.chc && !requestDocsModal.fullProcess"
                        @click="confirmRequestDocs">Enviar solicitação</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>

import axios from "axios"
import currencyTypes from "@/common/data/currency-types.data"
import creditorTypes from "@/common/data/creditor-types.data"
import statesList from "@/common/data/states-list.data"
import certificateStatus from "@/common/data/credit-certificate-status.data"
import justiceBranchTypes from "@/common/data/justice-branch-types.data"
import creditOriginTypes from "@/common/data/credit-certificate-credit-origin-types.data"
import reviewStatusTypes from "@/common/data/credit-certificate-review-status.data"
import { cpf, cnpj } from 'cpf-cnpj-validator'

export default {
    name: "CertificateCreditClaimDetails",

    data: () => ({
        user: null,
        page: {
            title: "Detalhes da certidão",
        },
        context: {
            id: null,
            mode: 'view',
        },
        requestDocsModal: {
            show: false,
            chc: false,
            fullProcess: false,
            loading: false,
        },
        dataset: {
            loading: false,
            saving: false,
            actionLoading: null,
            validForm: true,
            breadcrumbItems: [],
            data: {},
            currencyTypes: currencyTypes,
            creditorTypes: creditorTypes,
            statesList: statesList,
            certificateStatus: certificateStatus,
            justiceBranchTypes: justiceBranchTypes,
            creditOriginTypes: creditOriginTypes,
            reviewStatusTypes: reviewStatusTypes,
            clients: [],
        },
        debtorsHeaders: [
            { text: "Caso", value: "client" },
        ],
        creditorsHeaders: [
            { text: "Nome", value: "name" },
            { text: "CPF/CNPJ", value: "registration" },
        ],
        creditsHeaders: [
            { text: "CPF/CNPJ", value: "registration" },
            { text: "Moeda", value: "currency" },
            { text: "Origem", value: "credit_origin" },
            { text: "Valor", value: "amount" },
            { text: "Classe informada", value: "class_type" },
            { text: "Classe revisada", value: "reviewed_class_type" },
            { text: "Revisão", value: "review_status" },
            { text: "Alerta/observação", value: "review_note" },
        ],
        attorneyFeesHeaders: [
            { text: "Nome", value: "name" },
            { text: "CPF", value: "registration" },
            { text: "OAB", value: "oab" },
            { text: "UF", value: "uf" },
            { text: "Valor", value: "amount" },
            { text: "Fixado em", value: "fixed_at" },
        ],
        expertFeesHeaders: [
            { text: "Nome", value: "name" },
            { text: "CPF/CNPJ", value: "registration" },
            { text: "Valor", value: "amount" },
            { text: "Fixado em", value: "fixed_at" },
        ],
    }),

    computed: {
        editing() {
            return this.context.mode === 'edit';
        },
        creditorsCount() {
            return (this.dataset.data.creditors || []).length;
        },
        creditsCount() {
            return (this.dataset.data.credits || []).length;
        },
        availableDocumentsCount() {
            return [
                this.dataset.data.chc_file_url,
                this.dataset.data.confidential_full_process_file_url,
                this.dataset.data.homologation_decision_file_url,
                this.dataset.data.calculation_spreadsheet_file_url,
            ].filter(Boolean).length;
        },
    },

    created() {
        this.loadContext();
    },

    methods: {
        loadContext() {
            const saved = sessionStorage.getItem('certificateCreditClaimContext');

            if (!saved) {
                this.$dialog.notify.error('Nenhuma certidão selecionada.', {
                    position: 'top-right',
                    timeout: 3000
                });
                this.$router.replace('/certidao-credito');
                return;
            }

            try {
                const ctx = JSON.parse(saved);
                this.context.id = ctx.id;
                this.context.mode = ctx.mode || 'view';
            } catch (e) {
                this.$router.replace('/certidao-credito');
                return;
            }

            this.loadData();
        },

        enableEdit() {
            this.context.mode = 'edit';
            sessionStorage.setItem('certificateCreditClaimContext', JSON.stringify({
                id: this.context.id,
                mode: 'edit'
            }));
        },

        cancelEdit() {
            this.context.mode = 'view';
            sessionStorage.setItem('certificateCreditClaimContext', JSON.stringify({
                id: this.context.id,
                mode: 'view'
            }));
            this.load();
        },

        async save() {
            if (this.$refs.form && !this.$refs.form.validate()) {
                this.$dialog.notify.error('Preencha os campos corretamente antes de salvar.', {
                    position: 'top-right',
                    timeout: 3000
                });
                return;
            }
            this.dataset.saving = true;
            try {
                await axios.patch(
                    `/api/admin/users-creditors/credit-certificate-requests/${this.context.id}`,
                    this.buildPayload()
                );
                this.$dialog.notify.success('Alterações salvas com sucesso', {
                    position: 'top-right',
                    timeout: 2000
                });
                await this.load();
                this.cancelEdit();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                });
            } finally {
                this.dataset.saving = false;
            }
        },

        openRequestDocsModal() {
            this.requestDocsModal.chc = false;
            this.requestDocsModal.fullProcess = false;
            this.requestDocsModal.show = true;
        },

        closeRequestDocsModal() {
            this.requestDocsModal.show = false;
        },

        async confirmRequestDocs() {
            this.requestDocsModal.loading = true;
            try {
                const documents = [];
                if (this.requestDocsModal.chc) documents.push(1);          // CHC
                if (this.requestDocsModal.fullProcess) documents.push(2);  // ConfidentialFullProcess

                if (!documents.length) {
                    this.$dialog.notify.warning('Selecione ao menos um documento', {
                        position: 'top-right',
                        timeout: 2000
                    });
                    this.requestDocsModal.loading = false;
                    return;
                }

                await axios.patch(
                    `/api/admin/users-creditors/credit-certificate-requests/${this.context.id}/request-documents`,
                    { documents }
                );
                this.$dialog.notify.success('Solicitação de documentos enviada com sucesso', {
                    position: 'top-right',
                    timeout: 2000
                });
                this.closeRequestDocsModal();
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                });
            } finally {
                this.requestDocsModal.loading = false;
            }
        },

        async changeStatus(statusCode) {
            this.dataset.actionLoading = statusCode;
            try {
                await axios.patch(
                    `/api/admin/users-creditors/credit-certificate-requests/${this.context.id}/status`,
                    { status: statusCode }
                );
                this.$dialog.notify.success('Status atualizado com sucesso', {
                    position: 'top-right',
                    timeout: 2000
                });
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                });
            } finally {
                this.dataset.actionLoading = null;
            }
        },

        buildPayload() {
            const d = this.dataset.data;
            const stripDigits = v => (v ? String(v).replace(/\D/g, '') : v);
            return {
                origin_process: d.origin_process,
                process_uf: d.process_uf,
                justice_branch: d.justice_branch,
                judging_body: d.judging_body,
                final_judgment_at: d.final_judgment_at,
                calculation_updated_at: d.calculation_updated_at,
                observations: d.observations,
                has_attorney_fees: d.has_attorney_fees ? 1 : 0,
                has_expert_fees: d.has_expert_fees ? 1 : 0,
                is_confidential: d.is_confidential ? 1 : 0,
                debtors: d.debtors || [],
                creditors: (d.creditors || []).map(c => ({
                    ...c,
                    registration: stripDigits(c.registration),
                })),
                credits: (d.credits || []).map(c => ({
                    ...c,
                    registration: stripDigits(c.registration),
                    review_status: c.review_status === undefined || c.review_status === null ? 0 : Number(c.review_status),
                })),
                attorneyFees: (d.attorneyFees || []).map(f => ({
                    ...f,
                    registration: stripDigits(f.registration),
                })),
                expertFees: (d.expertFees || []).map(e => ({
                    ...e,
                    registration: stripDigits(e.registration),
                })),
            };
        },

        getDebtorsHeaders() {
            return this.editing
                ? [...this.debtorsHeaders, { text: 'Ações', value: 'actions', width: 80 }]
                : this.debtorsHeaders;
        },

        getCreditorsHeaders() {
            return this.editing
                ? [...this.creditorsHeaders, { text: 'Ações', value: 'actions', width: 80 }]
                : this.creditorsHeaders;
        },

        getCreditsHeaders() {
            return this.editing
                ? [...this.creditsHeaders, { text: 'Ações', value: 'actions', width: 80 }]
                : this.creditsHeaders;
        },

        getAttorneyFeesHeaders() {
            return this.editing
                ? [...this.attorneyFeesHeaders, { text: 'Ações', value: 'actions', width: 80 }]
                : this.attorneyFeesHeaders;
        },

        getExpertFeesHeaders() {
            return this.editing
                ? [...this.expertFeesHeaders, { text: 'Ações', value: 'actions', width: 80 }]
                : this.expertFeesHeaders;
        },

        addDebtor() {
            if (!this.dataset.data.debtors) this.$set(this.dataset.data, 'debtors', []);
            this.dataset.data.debtors.push({ client_id: null });
        },
        removeDebtor(index) {
            this.dataset.data.debtors.splice(index, 1);
        },

        addCreditor() {
            if (!this.dataset.data.creditors) this.$set(this.dataset.data, 'creditors', []);
            this.dataset.data.creditors.push({ name: '', registration: '' });
        },
        removeCreditor(index) {
            this.dataset.data.creditors.splice(index, 1);
        },

        addCredit() {
            if (!this.dataset.data.credits) this.$set(this.dataset.data, 'credits', []);
            this.dataset.data.credits.push({
                registration: '',
                currency: null,
                credit_origin: null,
                amount: 0,
                class_type: null,
                reviewed_class_type: null,
                review_status: 0,
                review_note: ''
            });
        },
        removeCredit(index) {
            this.dataset.data.credits.splice(index, 1);
        },

        addAttorneyFee() {
            if (!this.dataset.data.attorneyFees) this.$set(this.dataset.data, 'attorneyFees', []);
            this.dataset.data.attorneyFees.push({ name: '', registration: '', oab: '', uf: '', amount: null, fixed_at: null });
        },
        removeAttorneyFee(index) {
            this.dataset.data.attorneyFees.splice(index, 1);
        },

        addExpertFee() {
            if (!this.dataset.data.expertFees) this.$set(this.dataset.data, 'expertFees', []);
            this.dataset.data.expertFees.push({ name: '', registration: '', amount: null, fixed_at: null });
        },
        removeExpertFee(index) {
            this.dataset.data.expertFees.splice(index, 1);
        },

        async loadData() {
            this.user = this.$root.user.data || this.$root.user;
            this.listBreadCrumbItems();
            await Promise.all([this.load(), this.loadClients()]);
        },

        listBreadCrumbItems() {
            this.dataset.breadcrumbItems = [
                { text: "Homepage", disabled: false, to: "/home", exact: true },
                { text: "Certidão Habilitação Crédito", disabled: false, to: "/certidao-credito", exact: true },
                { text: this.page.title, disabled: true, exact: true },
            ];
        },

        formatDate(date) {
            if (!date || date === "0000-00-00 00:00:00") return null;
            return this.$moment(date).format('DD/MM/YYYY');
        },

        toIsoDate(date) {
            if (!date || date === "0000-00-00 00:00:00") return null;
            return this.$moment(date).format('YYYY-MM-DD');
        },

        formatDateTime(date) {
            if (!date || date === "0000-00-00 00:00:00") return null;
            return this.$moment(date).format('DD/MM/YYYY HH:mm:ss');
        },

        cpfCnpjRule(value) {
            if (!value) return true;
            const digits = String(value).replace(/\D/g, '');
            if (digits.length === 11) return cpf.isValid(digits) || 'CPF inválido';
            if (digits.length === 14) return cnpj.isValid(digits) || 'CNPJ inválido';
            return 'CPF/CNPJ inválido';
        },

        formatCpfCnpj(value) {
            if (!value) return 'N/A';
            const digits = value.replace(/\D/g, '');
            return digits.length === 14 ? cnpj.format(digits) : cpf.format(digits);
        },

        formatAmount(amount) {
            if (amount === null || amount === undefined || amount === '') return 'N/A';
            return Number(amount).toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },

        formatCurrency(amount, currencyId) {
            if (amount === null || amount === undefined) return 'N/A';
            const c = this.dataset.currencyTypes.find(x => x.id === currencyId);
            const prefix = c ? c.currencyFormat : '';
            const value = Number(amount).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            return `${prefix} ${value}`.trim();
        },

        getRequesterTypeLabel(type) {
            if (type === 'user') return 'Credor';
            if (type === 'lawyer') return 'Procurador';
            if (type === 'debtor') return 'Devedora';
            return 'N/A';
        },

        getStatusLabel(status) {
            const matched = this.dataset.certificateStatus.find((e) => e.id === status);
            return matched ? matched.name : 'Desconhecido';
        },

        getStatusColor(status) {
            switch (status) {
                case 0: return 'warning';
                case 1: return 'success';
                case 2: return 'error';
                case 4: return '#7E55AB';
                case 5: return '#29B6F6';
                case 6: return '#FB8C00';
                default: return 'grey';
            }
        },

        getReviewStatusLabel(status) {
            const matched = this.dataset.reviewStatusTypes.find((e) => e.id === Number(status));
            return matched ? matched.name : 'Pendente de revisão';
        },

        getReviewStatusColor(status) {
            const value = Number(status);
            if (value === 1) return 'success';
            if (value === 2) return '#FB8C00';
            return 'warning';
        },

        getTypeName(typeId) {
            const t = this.dataset.creditorTypes.find(c => c.id === typeId);
            return t ? t.name : 'N/A';
        },

        getCurrencyName(currencyId) {
            const c = this.dataset.currencyTypes.find(x => x.id === currencyId);
            return c ? c.name : 'N/A';
        },

        getClientName(client_id) {
            if (!client_id) return 'N/A';
            const client = this.dataset.clients.find(s => s.id === client_id);
            return client ? client.name : `#${client_id}`;
        },

        async load() {
            this.dataset.loading = true;
            try {
                const result = await axios.get(
                    `/api/admin/users-creditors/credit-certificate-requests/${this.context.id}`
                );
                const content = result.data.content || {};
                if (content.final_judgment_at) {
                    content.final_judgment_at = this.toIsoDate(content.final_judgment_at);
                }
                if (content.calculation_updated_at) {
                    content.calculation_updated_at = this.toIsoDate(content.calculation_updated_at);
                }
                if (content.requester_registration) {
                    content.requester_registration = this.formatCpfCnpj(content.requester_registration);
                }
                (content.creditors || []).forEach(c => {
                    if (c.registration) c.registration = this.formatCpfCnpj(c.registration);
                });
                (content.credits || []).forEach(c => {
                    if (c.registration) c.registration = this.formatCpfCnpj(c.registration);
                    if (c.review_status === undefined || c.review_status === null) c.review_status = 0;
                });
                (content.attorneyFees || []).forEach(f => {
                    if (f.registration) f.registration = this.formatCpfCnpj(f.registration);
                    if (f.fixed_at) f.fixed_at = this.toIsoDate(f.fixed_at);
                });
                (content.expertFees || []).forEach(e => {
                    if (e.registration) e.registration = this.formatCpfCnpj(e.registration);
                    if (e.fixed_at) e.fixed_at = this.toIsoDate(e.fixed_at);
                });
                this.dataset.data = content;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            } finally {
                this.dataset.loading = false;
            }
        },

        async loadClients() {
            try {
                const result = await axios.get(`/api/clients`, { params: { start: 1, limit: 100000, search: {} } });
                this.dataset.clients = result.data.content.data;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            }
        },
    },
};
</script>

<style scoped>
.chc-analysis {
    min-height: calc(100vh - 48px);
    color: #0f172a;
    background: #f8fafc;
    font-family: "Plus Jakarta Sans", "Roboto", sans-serif;
}
.chc-analysis__page-header { display:flex; align-items:flex-start; justify-content:space-between; gap:20px; margin-bottom:18px; }
.chc-analysis__page-header h1 { margin:0; color:#0f172a; font-size:25px; line-height:1.25; font-weight:700; letter-spacing:-.025em; }
.chc-analysis__page-header p { margin:6px 0 0; color:#64748b; font-size:12px; }
.chc-analysis__page-actions { display:flex; align-items:center; gap:8px; }
.chc-analysis__page-actions .v-btn { min-width:0; height:36px; padding:0 14px; border-radius:6px; font-size:11px; font-weight:600; letter-spacing:0; text-transform:none; }
.chc-analysis__skeleton { padding:20px; border:1px solid #dfe5ec; border-radius:8px; background:#fff; }
.chc-analysis__summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 12px;
    padding: 14px 16px;
    border: 1px solid #dfe5ec;
    border-radius: 8px;
    background: #fff;
}
.chc-analysis__summary-main { display:grid; grid-template-columns:auto auto minmax(180px,1fr); align-items:center; gap:6px 10px; }
.chc-analysis__summary-main > .v-chip { height:22px; font-size:8px; font-weight:700; text-transform:uppercase; }
.chc-analysis__requester { display:flex; flex-direction:column; }
.chc-analysis__requester strong { color:#172033; font-size:11px; font-weight:700; }
.chc-analysis__requester span { margin-top:2px; color:#64748b; font-size:9px; }
.chc-analysis__summary small { display:flex; grid-column:1/-1; align-items:center; gap:5px; color:#64748b; font-size:9px; }
.chc-analysis__protocol { color:#64748b; font-family:monospace; font-size:10px; }
.chc-analysis__summary > .v-btn { min-width:0; height:32px; padding:0 10px; font-size:10px; font-weight:600; letter-spacing:0; text-transform:none; }
.chc-analysis__metrics {
    display:grid;
    grid-template-columns:repeat(3, minmax(0, 1fr));
    gap:8px;
    margin-bottom:12px;
}
.chc-analysis__metrics > div { display:flex; align-items:center; gap:9px; min-height:58px; padding:10px 12px; border:1px solid #dfe5ec; border-radius:8px; background:#fff; }
.chc-analysis__metrics > div > span:last-child { display:flex; flex-direction:column; }
.chc-analysis__metrics small { color:#64748b; font-size:8px; }
.chc-analysis__metrics strong { margin-top:2px; color:#0f172a; font-size:14px; }
.chc-analysis__metric-icon { display:inline-flex; align-items:center; justify-content:center; width:30px; height:30px; color:#014373; border-radius:6px; background:#eef5f9; }
.chc-analysis__grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 330px;
    align-items: start;
    gap: 12px;
}
.chc-analysis__grid > .v-row { grid-column:1; width:100%; margin:0 0 2px; }
.chc-analysis__grid > .v-row > .col { padding:0; }
.chc-analysis__main { grid-column: 1 !important; grid-row: 1; }
.chc-analysis__section-card { overflow:hidden; border-color:#dfe5ec !important; border-radius:8px !important; box-shadow:0 1px 2px rgba(15,23,42,.025) !important; }
.chc-analysis__section-card .card-group-title {
    display:flex;
    align-items:center;
    min-height:54px;
    padding:12px 14px;
    color:#0f172a;
    border-bottom:1px solid #edf1f5;
    font-size:12px;
}
.chc-analysis__section-card .card-group-title > span:not(.chc-analysis__section-icon) { display:flex; flex-direction:column; }
.chc-analysis__section-card .card-group-title strong { font-size:12px; line-height:1.3; }
.chc-analysis__section-card .card-group-title small { margin-top:2px; color:#64748b; font-size:8px; font-weight:400; }
.chc-analysis__section-icon { display:inline-flex; align-items:center; justify-content:center; width:30px; height:30px; margin-right:9px; color:#014373; border-radius:6px; background:#eef5f9; }
.chc-analysis__section-card > .v-card__text { padding:14px !important; }
.chc-analysis__section-card .row { margin:-5px; }
.chc-analysis__section-card .col { padding:5px; }
.chc-analysis__section-card ::v-deep .v-input__slot { min-height:42px !important; border-radius:6px !important; }
.chc-analysis__section-card ::v-deep input,
.chc-analysis__section-card ::v-deep textarea,
.chc-analysis__section-card ::v-deep .v-label,
.chc-analysis__section-card ::v-deep .v-select__selection { font-family:"Plus Jakarta Sans",sans-serif !important; font-size:10px !important; }
.chc-analysis__section-card ::v-deep .v-input--is-disabled { opacity:1; }
.chc-analysis__section-card ::v-deep .v-input--is-disabled .v-input__slot { background:#f8fafc !important; }
.chc-analysis__section-card ::v-deep .v-input--is-disabled input { color:#334155 !important; }
.chc-analysis__section-card ::v-deep .v-data-table { border:1px solid #edf1f5; border-radius:7px; }
.chc-analysis__section-card ::v-deep .v-data-table thead th { height:38px; color:#64748b !important; background:#f8fafc; font-size:9px; font-weight:600; }
.chc-analysis__section-card ::v-deep .v-data-table tbody td { height:46px; color:#334155; font-size:10px; border-bottom:1px solid #edf1f5 !important; }
.chc-analysis__section-card ::v-deep .v-btn { border-radius:6px; font-size:9px; letter-spacing:0; text-transform:none; }
.chc-analysis__documents .v-card__text > .row { margin:-5px; }
.chc-analysis__document-card { min-height:108px; }
.chc-analysis__document-card > div:first-child { color:#334155; font-size:10px; }
.chc-analysis__document-card > div:nth-child(2) { height:calc(100% - 24px); padding:10px; border:1px solid #edf1f5; border-radius:7px; background:#f8fafc; color:#64748b; font-size:9px; }
.chc-analysis__document-card .v-btn { height:30px; padding:0 10px; background:#fff !important; }
.chc-analysis__decision {
    position: sticky;
    top: 12px;
    grid-column: 2 !important;
    grid-row: 1;
}
.chc-analysis__decision-actions { display:flex; flex-direction:column; gap:7px; }
.chc-analysis__decision-actions > div,
.chc-analysis__decision-actions button { width: 100%; }
.chc-analysis__decision .status-actions-card { width:100%; overflow:hidden; border-color:#dfe5ec !important; border-radius:8px; background:#fff; box-shadow:0 4px 16px rgba(15,23,42,.04); }
.chc-analysis__decision .status-actions-title { min-height:54px; padding:12px 14px; border-bottom:1px solid #edf1f5; }
.chc-analysis__decision .status-actions-title span { display:flex; flex-direction:column; }
.chc-analysis__decision .status-actions-title strong { font-size:12px; }
.chc-analysis__decision .status-actions-title small { margin-top:2px; color:#64748b; font-size:8px; font-weight:400; }
.chc-analysis__decision .v-card__text { padding:12px !important; }
.chc-analysis__decision-status { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:12px; padding-bottom:10px; border-bottom:1px solid #edf1f5; }
.chc-analysis__decision-status > span { color:#64748b; font-size:9px; font-weight:600; }
.chc-analysis__decision-status .v-chip { height:22px; font-size:8px; font-weight:700; text-transform:uppercase; }
.chc-analysis__decision-actions .v-btn { height:34px; border-radius:6px; font-size:9px; font-weight:700; letter-spacing:0; text-transform:none; }
.chc-analysis__decision-actions #btnReject { color:#b42318 !important; background:#fff !important; border:1px solid #fecaca; }
.chc-analysis__decision-actions #btnRequestDocs { color:#6941c6 !important; background:#fff !important; border:1px solid #ddd6fe; }
.chc-analysis__decision-actions #btnOutdated { color:#b54708 !important; background:#fff !important; border:1px solid #fed7aa; }
.chc-analysis__decision-actions #btnRestore { color:#475569 !important; background:#fff !important; border:1px solid #dfe5ec; }
.chc-analysis ::v-deep .app-breadcrumb { padding:0 !important; }
.chc-analysis ::v-deep .app-breadcrumb li,
.chc-analysis ::v-deep .app-breadcrumb a { color:#64748b !important; font-size:9px; }
.chc-analysis ::v-deep .v-dialog .v-card { border-radius:10px; }
.chc-analysis ::v-deep .v-dialog .v-card__title { justify-content:flex-start !important; padding:18px 20px 10px !important; border:0 !important; color:#0f172a; font-size:16px; font-weight:700; }
.chc-analysis ::v-deep .v-dialog .v-card__text { padding:8px 20px 16px !important; color:#475569; font-size:11px; }
.chc-analysis ::v-deep .v-dialog .v-card__actions { padding:12px 20px !important; border-top:1px solid #edf1f5; }
.chc-analysis ::v-deep .v-dialog .v-card__actions .v-btn { min-width:0; height:34px; padding:0 12px; border-radius:6px; font-size:10px; letter-spacing:0; text-transform:none; }
.chc-analysis__document-option { display:flex; align-items:center; gap:10px; margin-top:10px; padding:10px 12px; border:1px solid #dfe5ec; border-radius:7px; cursor:pointer; }
.chc-analysis__document-option:hover { border-color:#9fb8ca; background:#f8fafc; }
.chc-analysis__document-option .v-input { flex:0 0 auto; margin:0; padding:0; }
.chc-analysis__document-option > span { display:flex; flex-direction:column; }
.chc-analysis__document-option strong { color:#172033; font-size:10px; }
.chc-analysis__document-option small { margin-top:2px; color:#64748b; font-size:8px; }
::v-deep .app-table tbody tr td {
    border-bottom: 1px solid #edf1f5 !important;
}
@media (max-width: 1000px) {
    .chc-analysis__grid { grid-template-columns: 1fr; }
    .chc-analysis__decision { position: static; grid-column: 1 !important; grid-row: 2; }
    .chc-analysis__summary { align-items: flex-start; flex-direction: column; }
}
@media (max-width: 700px) {
    .chc-analysis { padding:16px !important; }
    .chc-analysis__page-header { flex-direction:column; }
    .chc-analysis__page-actions { width:100%; }
    .chc-analysis__summary-main { grid-template-columns:auto 1fr; }
    .chc-analysis__requester { grid-column:1/-1; }
    .chc-analysis__metrics { grid-template-columns:1fr; }
}
</style>
